import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Hospital, ClipboardList, Clock, IndianRupee, CheckCircle2, ExternalLink, ListChecks,
  AlertTriangle, Users, FileDown
} from "lucide-react";

const PRIORITIES = [
  { id: "standard", label: "Written",  fee: 1000, sla: "48h", help: "Written second opinion" },
  { id: "video",    label: "Video",    fee: 2000, sla: "24h", help: "Live tele-consult" },
];

export default function HospitalDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending_payment: 0, in_review: 0, spent_inr: 0 });
  const [discount, setDiscount] = useState({ current_month_pct: 0, label: "", tiers: [] });
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, d, disc] = await Promise.all([
        api.get("/hospital/cases"),
        api.get("/hospital/stats"),
        api.get("/doctors"),
        api.get("/hospital/me/discount"),
      ]);
      setCases(c.data || []);
      setStats(s.data || {});
      setDoctors(d.data || []);
      setDiscount(disc.data || {});
    } catch (e) {
      // pending account → backend already returned 403, our axios interceptor will surface it
      console.warn("hospital load:", e?.response?.data || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.status === "pending") return; // pending hospitals can't list cases
    // Defer to a microtask so we don't trigger set-state synchronously inside the effect body
    Promise.resolve().then(load);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.status === "pending") {
    return <PendingState/>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase tracking-wider border border-blue-200">
              <Hospital className="w-3 h-3"/> Hospital partner
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              {user?.organization_name || user?.name}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {user?.organization_city ? `${user.organization_city}, ` : ""}{user?.organization_state || ""}
            </p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="h-new-case-btn">
            <Plus className="w-4 h-4 mr-2"/> Submit new case
          </Button>
        </div>

        {/* KPI strip */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={ClipboardList} label="Total cases"    value={stats.total}            colour="text-slate-700 bg-slate-100"/>
          <Kpi icon={Clock}         label="In review"      value={stats.in_review}        colour="text-blue-700 bg-blue-100"/>
          <Kpi icon={CheckCircle2}  label="Completed"      value={stats.completed}        colour="text-emerald-700 bg-emerald-100"/>
          <Kpi icon={IndianRupee}   label="Spend (₹)"      value={(stats.spent_inr || 0).toLocaleString("en-IN")} colour="text-amber-700 bg-amber-100"/>
        </div>

        {/* Volume discount banner */}
        {discount.current_month_pct > 0 ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 flex items-center gap-3" data-testid="h-discount-active">
            <span className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
              <IndianRupee className="w-5 h-5"/>
            </span>
            <div className="text-sm text-slate-800">
              <div className="font-heading font-semibold text-emerald-800">{discount.label}</div>
              <div className="text-xs text-slate-600">You&apos;re saving {discount.current_month_pct}% on every case you submit this month. Resets on the 1st.</div>
            </div>
          </div>
        ) : (discount.tiers || []).length > 0 ? (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="h-discount-tiers">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Volume discount tiers</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {discount.tiers.map((t) => (
                <span key={t.threshold} className="text-[11px] px-2 py-1 rounded-full bg-slate-50 text-slate-700 border border-slate-200">
                  {t.threshold}+ cases/mo · <strong className="text-emerald-700">−{t.pct}%</strong>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* Monthly statement PDF export — for accounts / finance teams */}
        <MonthlyStatement />

        {/* Cases */}
        <Card className="mt-6 p-0 border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2 font-heading font-semibold text-slate-900">
              <ListChecks className="w-4 h-4 text-blue-700"/> Submitted cases
            </div>
            <span className="text-xs text-slate-500">{cases.length}</span>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500 text-sm">Loading cases…</div>
          ) : cases.length === 0 ? (
            <EmptyState onClick={() => setShowForm(true)} />
          ) : (
            <div className="divide-y divide-slate-100">
              {cases.map((c) => <CaseRow key={c.id} c={c} onView={() => nav(`/case/${c.id}`)}/>)}
            </div>
          )}
        </Card>
      </div>

      <NewCaseDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        doctors={doctors}
        discount={discount}
        onCreated={(c) => { setShowForm(false); load(); toast.success("Case submitted"); nav(`/case/${c.id}`); }}
      />
    </div>
  );
}

function MonthlyStatement() {
  const [period, setPeriod] = React.useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);

  const loadPreview = React.useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await api.get(`/hospital/statement/${p}`);
      setPreview(r.data);
    } catch {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { loadPreview(period); }, [period, loadPreview]);

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("noi_token") || "";
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const res = await fetch(`${backend}/api/hospital/statement/${period}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `neuroopinion-statement-${period}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error("Could not download statement. Try again in a moment.");
    } finally {
      setDownloading(false);
    }
  };

  // Build a lookback list of the last 6 months
  const monthOptions = React.useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const lbl = d.toLocaleString("en-US", { month: "long", year: "numeric" });
      out.push({ value: val, label: lbl });
    }
    return out;
  }, []);

  return (
    <Card className="mt-6 p-5 border-slate-200" data-testid="hospital-monthly-statement">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 font-heading font-semibold text-slate-900">
            <FileDown className="w-4 h-4 text-blue-700"/> Monthly statement
          </div>
          <p className="text-xs text-slate-500 mt-1">Downloadable PDF invoice for your finance / accounts team.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-44" data-testid="statement-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={download}
            disabled={downloading || loading || !preview || preview.case_count === 0}
            className="bg-blue-700 hover:bg-blue-800 h-9"
            data-testid="statement-download-btn"
          >
            <FileDown className="w-4 h-4 mr-1.5"/>
            {downloading ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Cases in period</div>
          <div className="mt-0.5 font-heading text-xl font-bold text-slate-900" data-testid="statement-case-count">
            {loading ? "—" : (preview?.case_count ?? 0)}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Subtotal</div>
          <div className="mt-0.5 font-heading text-xl font-bold text-slate-900" data-testid="statement-subtotal">
            {loading ? "—" : `₹${(preview?.subtotal_inr ?? 0).toLocaleString("en-IN")}`}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Statement for</div>
          <div className="mt-0.5 font-heading text-base font-semibold text-slate-900 truncate">
            {preview?.display || period}
          </div>
        </div>
      </div>
      {!loading && preview && preview.case_count === 0 && (
        <p className="mt-3 text-xs text-slate-500">No paid cases this month — nothing to invoice yet.</p>
      )}
    </Card>
  );
}


function Kpi({ icon: Icon, label, value, colour }) {
  return (
    <Card className="p-4 border-slate-200">
      <div className="flex items-center gap-3">
        <span className={`shrink-0 w-10 h-10 rounded-lg grid place-items-center ${colour}`}>
          <Icon className="w-5 h-5"/>
        </span>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-xl font-heading font-bold text-slate-900">{value ?? 0}</div>
        </div>
      </div>
    </Card>
  );
}

function CaseRow({ c, onView }) {
  const colour = {
    pending_payment: "bg-amber-100 text-amber-800 border-amber-200",
    awaiting_review: "bg-blue-100 text-blue-800 border-blue-200",
    under_review:    "bg-blue-100 text-blue-800 border-blue-200",
    completed:       "bg-emerald-100 text-emerald-800 border-emerald-200",
    refunded:        "bg-slate-100 text-slate-700 border-slate-200",
  }[c.status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <div className="px-5 py-3 flex items-center gap-4 hover:bg-slate-50" data-testid={`h-case-row-${c.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-slate-900 truncate">{c.patient_profile_name || c.patient_name}</div>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${colour}`}>
            {(c.status || "").replace(/_/g, " ")}
          </span>
          {c.priority === "urgent" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 font-semibold uppercase">Urgent</span>}
          {c.priority === "video" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 font-semibold uppercase">Video</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {c.internal_ref_id ? <span className="font-mono mr-2">{c.internal_ref_id}</span> : null}
          {c.symptoms?.slice(0, 80)}{c.symptoms && c.symptoms.length > 80 ? "…" : ""}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5">
          {c.referring_clinician ? `Ref: ${c.referring_clinician} · ` : ""}
          submitted {new Date(c.created_at).toLocaleString()} · ₹{c.fee_after_discount ?? c.fee}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onView} data-testid={`h-case-view-${c.id}`}>
        Open <ExternalLink className="w-3.5 h-3.5 ml-1"/>
      </Button>
    </div>
  );
}

function EmptyState({ onClick }) {
  return (
    <div className="p-10 text-center" data-testid="h-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-100 text-blue-700 grid place-items-center">
        <ClipboardList className="w-7 h-7"/>
      </div>
      <h3 className="font-heading font-semibold text-slate-900 mt-3">No cases submitted yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        Submit your first patient case in under a minute. Reports are returned in 48 hours (12 h for urgent cases).
      </p>
      <Button onClick={onClick} className="mt-4 bg-blue-700 hover:bg-blue-800">
        <Plus className="w-4 h-4 mr-2"/> Submit first case
      </Button>
    </div>
  );
}

function PendingState() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-100 text-amber-700 grid place-items-center">
          <AlertTriangle className="w-8 h-8"/>
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">Account under review</h1>
        <p className="text-slate-600 mt-3 max-w-md mx-auto">
          Our admin team is reviewing your hospital application. You&apos;ll receive an email the moment your
          partner account is approved — usually within 24 hours.
        </p>
      </div>
    </div>
  );
}

function NewCaseDialog({ open, onClose, doctors, onCreated, discount }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    patient_name: "", patient_age: "", patient_sex: "M", patient_phone: "",
    symptoms: "", priority: "standard", doctor_id: "",
    referring_clinician: "", internal_ref_id: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const baseFee = useMemo(() => PRIORITIES.find((p) => p.id === form.priority)?.fee, [form.priority]);
  const sla = useMemo(() => PRIORITIES.find((p) => p.id === form.priority)?.sla, [form.priority]);
  const dPct = discount?.current_month_pct || 0;
  const fee = dPct > 0 ? Math.round(baseFee * (1 - dPct / 100)) : baseFee;

  const submit = async () => {
    if (!form.patient_name.trim()) { toast.error("Patient name is required"); return; }
    if (form.symptoms.trim().length < 10) { toast.error("Add at least a 1-line symptom summary"); return; }
    setBusy(true);
    try {
      const payload = {
        patient_name: form.patient_name.trim(),
        patient_age: form.patient_age ? Number(form.patient_age) : null,
        patient_sex: form.patient_sex,
        patient_phone: form.patient_phone || null,
        symptoms: form.symptoms.trim(),
        priority: form.priority,
        doctor_id: form.doctor_id || null,
        referring_clinician: form.referring_clinician || null,
        internal_ref_id: form.internal_ref_id || null,
        language: "en",
      };
      const r = await api.post("/hospital/cases", payload);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit case");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="h-new-case-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Plus className="w-5 h-5 text-blue-700"/> Submit new case
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            One patient per case. You can upload MRI/CT files inside the case after creating it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3 mt-1">
          <Field label="Patient name *" v={form.patient_name} set={(v) => set("patient_name", v)} testId="h-pt-name"/>
          <Field label="Age" v={form.patient_age} set={(v) => set("patient_age", v.replace(/\D/g, ""))} testId="h-pt-age" placeholder="e.g. 52"/>
          <div>
            <Label className="text-slate-700">Sex</Label>
            <Select value={form.patient_sex} onValueChange={(v) => set("patient_sex", v)}>
              <SelectTrigger className="mt-1" data-testid="h-pt-sex"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="M">Male</SelectItem>
                <SelectItem value="F">Female</SelectItem>
                <SelectItem value="O">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Patient phone (optional)" v={form.patient_phone} set={(v) => set("patient_phone", v)} testId="h-pt-phone" placeholder="+91 …"/>
          <Field label="Referring clinician" v={form.referring_clinician} set={(v) => set("referring_clinician", v)} testId="h-pt-ref" placeholder="Dr. R. Iyer"/>
          <Field label="Internal MRN" v={form.internal_ref_id} set={(v) => set("internal_ref_id", v)} testId="h-pt-mrn" placeholder="MRN-99821"/>

          <div className="sm:col-span-2">
            <Label className="text-slate-700">Symptom summary *</Label>
            <Textarea rows={3} value={form.symptoms} onChange={(e) => set("symptoms", e.target.value)} className="mt-1"
                      placeholder="Sudden onset right-sided weakness, slurred speech, started 6 hours ago…"
                      data-testid="h-pt-symptoms"/>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-slate-700">Priority</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {PRIORITIES.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => set("priority", p.id)}
                  data-testid={`h-priority-${p.id}`}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    form.priority === p.id
                      ? "border-blue-700 bg-blue-50 ring-2 ring-blue-100"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-900">{p.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{p.help}</div>
                  <div className="text-[11px] mt-1.5 font-mono">₹{p.fee} · {p.sla}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <Label className="text-slate-700">Assign to a specific specialist (optional)</Label>
            <Select value={form.doctor_id} onValueChange={(v) => set("doctor_id", v)}>
              <SelectTrigger className="mt-1" data-testid="h-pt-doctor"><SelectValue placeholder="Let NeuroOpinion assign the best fit"/></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} · {d.specialization}{d.is_featured ? " · Founder" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Leave blank for fastest turnaround.</p>
          </div>
        </div>

        <DialogFooter className="mt-2 sm:justify-between">
          <div className="text-xs text-slate-500 self-center">
            <Users className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5"/>
            Patient consent collected by hospital under B2B MSA. We never contact the patient directly.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="h-new-case-submit">
              {busy ? "Submitting…" : (
                dPct > 0
                  ? <>Submit case — <span className="line-through opacity-60 mr-1">₹{baseFee}</span>₹{fee} · {sla}</>
                  : `Submit case — ₹${fee} · ${sla}`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, set, type = "text", placeholder, testId }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <Input type={type} value={v} onChange={(e) => set(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={testId}/>
    </div>
  );
}
